import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import {
  ElderBlobViewerComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderToastService,
  ElderTranslateService,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { WorkItem } from '../../../../orders/work-items/work-item'
import { PrintButtonComponent } from '../../../../output/print/print-button/print-button.component'
import { PrintCommandDirective } from '../../../../output/print/print-command.directive'
import { PrintOptions, ReportSpec } from '../../../../output/report/report-job'
import { WorkInstructionService } from '../../../../process/process-plans/instructions/work-instruction.service'
import { ControlTerminal } from '../../../control-terminal'
import { WorkInstructionPage } from './work-instruction-page'
import { sum } from '../../../../../../functional.util'

@Component({
  selector: 'app-terminal-work-item-instructions',
  templateUrl: './terminal-work-item-instructions.component.html',
  styleUrls: ['./terminal-work-item-instructions.component.scss'],
  imports: [
    PrintButtonComponent,
    PrintCommandDirective,
    MatButton,
    MatIcon,
    ElderBlobViewerComponent,
    AsyncPipe,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
  ],
})
export class TerminalWorkItemInstructionsComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly item$ = new BehaviorSubject<WorkItem | null>(null)

  public readonly workCenterId$ = new BehaviorSubject<string | null>(null)
  public readonly controlTerminal$ = new BehaviorSubject<ControlTerminal | null>(null)

  public readonly instructionPages$ = new BehaviorSubject<WorkInstructionPage[]>([])

  private readonly _currentInstructionIndex$ = new BehaviorSubject<number | null>(null)
  public readonly currentInstructionPage$ = new BehaviorSubject<WorkInstructionPage | null>(null)
  public hasNext: boolean | undefined
  public hasPrevious: boolean | undefined

  public readonly labelReport$: Observable<ReportSpec>
  public readonly documentPrintOptions$: Observable<PrintOptions>

  public readonly printableMimeTypes = ['application/pdf']
  public readonly printingEnabled$ = new BehaviorSubject<boolean>(false)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const instructionsService = inject(WorkInstructionService)
    const toastService = inject(ElderToastService)
    const translate = inject(ElderTranslateService)
    combineLatest([this.item$, this.workCenterId$])
      .pipe(takeUntilDestroyed())
      .subscribe(([item, workCenterId]) => {
        if (item && workCenterId) {
          instructionsService.getOperations(item.id, workCenterId).subscribe({
            next: (operations) => {
              const workInstructionPages = operations.flatMap((op) =>
                op.instructions.map(
                  (inst) =>
                    <WorkInstructionPage>{
                      action: op.action,
                      operationIndex: op.index,
                      instruction: inst,
                    }
                )
              )
              this.instructionPages$.next(workInstructionPages)
              if (workInstructionPages.length > 0) {
                this.currentInstructionIndex = 0
              }
              const resolutionErrors = operations
                .flatMap((op) => op.instructions)
                .map((instr) => instr.resolutionErrors)
                .reduce(sum, 0)
              if (resolutionErrors > 0) {
                const msg = translate.instant('attachments.resolutionError', {
                  count: resolutionErrors,
                })
                toastService.warn(msg)
              }
            },
            error: (err) => toastService.error('Could not load instructions!', err),
          })
        }
      })

    this.labelReport$ = combineLatest([
      this.item$,
      this.workCenterId$,
      this.currentInstructionPage$,
    ]).pipe(
      takeUntilDestroyed(),
      map(([item, workCenterId, page]) =>
        ReportSpec.workInstructionReport(
          item.id,
          workCenterId,
          page.instruction.taskName,
          page.action
        )
      )
    )

    this.documentPrintOptions$ = this.controlTerminal$.pipe(
      filter((terminal) => !!terminal?.documentPrinter),
      map((terminal) => PrintOptions.printer(terminal!.documentPrinter.printerId))
    )

    this._currentInstructionIndex$.subscribe({
      next: (instructionIndex) => {
        const instructions = this.instructionPages$.getValue()
        if (instructions.length > 0) {
          this.updateHasNextAndPrevious(instructionIndex)
          this.currentInstructionPage$.next(this.instructionPages$.getValue()[instructionIndex])
        }
      },
    })

    this.currentInstructionPage$.subscribe({
      next: (currentPage) => {
        const isInstructionSupported = !!currentPage?.instruction?.attachments?.some((att) =>
          this.printableMimeTypes.includes(att.mimeType)
        )
        const isPrintingSupported = !!this.controlTerminal?.documentPrinter?.printerId
        this.log.debug(
          `printing enabled: printing supported: ${isPrintingSupported}, instruction supported: ${isInstructionSupported}`
        )
        this.printingEnabled$.next(isInstructionSupported && isPrintingSupported)
      },
    })
  }

  /***************************************************************************
   *                                                                         *
   * Getters / Setters                                                       *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set item(item: WorkItem) {
    this.item$.next(item)
  }

  @Input()
  public set workCenterId(workCenterId: string) {
    this.workCenterId$.next(workCenterId)
  }

  @Input()
  public set controlTerminal(controlTerminal: ControlTerminal) {
    this.log.debug(`Setting controlTerminal: ${controlTerminal?.controlCenterId}`)
    this.controlTerminal$.next(controlTerminal)
  }

  public get controlTerminal(): ControlTerminal | null {
    return this.controlTerminal$.getValue()
  }

  private set currentInstructionIndex(index: number) {
    this._currentInstructionIndex$.next(index)
  }

  public get currentInstructionIndex$(): Observable<number | null> {
    return this._currentInstructionIndex$.asObservable()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public nextInstruction(): void {
    const newIndex = this.getCurrentInstructionIndex() + 1
    const instructions = this.instructionPages$.getValue()
    if (newIndex < instructions.length) {
      this.currentInstructionIndex = newIndex
    }
  }

  public previousInstruction(): void {
    const newIndex = this.getCurrentInstructionIndex() - 1
    if (newIndex >= 0) {
      this.currentInstructionIndex = newIndex
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private getCurrentInstructionIndex(): number {
    const instructions = this.instructionPages$.getValue()
    return instructions.indexOf(this.currentInstructionPage$.getValue())
  }

  private updateHasNextAndPrevious(index: number): void {
    const numOfInstructions = this.instructionPages$.getValue().length
    this.hasNext = index < numOfInstructions - 1
    this.hasPrevious = index > 0
  }
}
